import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { landingAPI } from '../../api';
import styles from './LandingPage.module.css';
import styled from 'styled-components';

const ResponsiveContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
`;

const ResponsiveImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  position: relative;
`;

const ClickArea = styled.div`
  position: absolute;
  cursor: pointer;
  animation: pulse 2s infinite ease-in-out;
  /* background-color: rgba(255,0 ,0, 0.3); */
  
  @keyframes pulse {
    0% { 
      background-color: rgba(255, 255, 255, 0);
      box-shadow: 0 0 0 rgba(255, 255, 255, 0);
    }
    50% { 
      background-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
    }
    100% { 
      background-color: rgba(255, 255, 255, 0);
      box-shadow: 0 0 0 rgba(255, 255, 255, 0);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Q_MainLandingPage: React.FC<{pageId: string}> = ({pageId}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  const yy = 3823 - 3431;
  const [areas] = useState([
    { id: '크크벳', url: 'https://크크벳접속.com', coords: [26, 988+yy, 979, 1112+yy], shape: 'rect' as const },
    { id: '대빵', url: 'https://대빵.com', coords: [28, 1124+yy, 977, 1251+yy], shape: 'rect' as const },
    { id: '펩시접속', url: 'https://펩시접속.com', coords: [29, 1265+yy, 978, 1391+yy], shape: 'rect' as const },
    { id: '에이피', url: 'https://에이피.com', coords: [30, 1405+yy, 980, 1521+yy], shape: 'rect' as const },
    { id: '야옹이', url: 'https://야옹이.com', coords: [28, 1539+yy, 980, 1658+yy], shape: 'rect' as const },
    { id: '반딧불', url: 'https://반딧불.com', coords: [29, 1681+yy, 981, 1799+yy], shape: 'rect' as const },
    { id: '에그에그', url: 'https://에그에그.com', coords: [29, 1816+yy, 978, 1940+yy], shape: 'rect' as const },
    { id: '이태리', url: 'https://이태리.com', coords: [26, 1956+yy, 980, 2078+yy], shape: 'rect' as const },
    { id: '정글접속', url: 'https://정글접속.com', coords: [29, 2099+yy, 979, 2215+yy], shape: 'rect' as const },
    { id: '벳마트', url: 'https://벳마트.com', coords: [29, 2239+yy, 984, 2362+yy], shape: 'rect' as const },
    { id: '당근접속', url: 'https://당근접속.com', coords: [28, 2381+yy, 978, 2505+yy], shape: 'rect' as const },
    { id: '썬뱃', url: 'https://썬뱃.com', coords: [27, 2520+yy, 981, 2648+yy], shape: 'rect' as const },
    { id: '땅콩접속', url: 'https://땅콩접속.com', coords: [26, 2660+yy, 981, 2785+yy], shape: 'rect' as const },
    { id: '돛단배', url: 'https://돛단배.com', coords: [24, 2804+yy, 980, 2928+yy], shape: 'rect' as const },
    { id: '허슬러접속', url: 'https://허슬러접속.com', coords: [23, 3132+yy, 978, 3369+yy], shape: 'rect' as const }
  ]);
  const [clickAreas, setClickAreas] = useState<{id: string; style: {left: string; top: string; width: string; height: string;}}[]>([]);
  
  // 원본 이미지 크기 정의
  const originalWidth = 1000;
  const originalHeight = 3431;

  useEffect(() => {
    // 모바일 여부 확인
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    // 페이지 조회수 증가
    const recordView = async () => {
      try {
        await landingAPI.recordView(pageId || 'QLANDING');
      } catch (error) {
        console.error('페이지 조회수 기록 중 오류 발생:', error);
      }
    };

    recordView();
  }, [pageId]);

  // 링크 클릭 핸들러
  const handleLinkClick = async (linkId: string, url: string) => {
    setLoading(true);
    try {
      landingAPI.recordClick(pageId!, linkId);
    } catch (error) {
    }
    setTimeout(()=>setLoading(false), 1000);
  };

  // 좌표 조정 함수
  const adjustCoords = () => {
    if (!imgRef.current) return;
    
    const currentWidth = imgRef.current.clientWidth;
    const ratio = currentWidth / originalWidth;
    
    const newClickAreas = areas.map(area => {
      const [x1, y1, x2, y2] = area.coords;
      return {
        id: area.id,
        style: {
          left: `${(x1 * ratio)}px`,
          top: `${(y1 * ratio)}px`,
          width: `${(x2 - x1) * ratio}px`,
          height: `${(y2 - y1) * ratio}px`
        }
      };
    });
    
    setClickAreas(newClickAreas);
  };

  // 컴포넌트 마운트 및 리사이즈 시 좌표 조정
  useEffect(() => {
    // 이미지 로드 완료 시 좌표 조정
    if (imgRef.current) {
      imgRef.current.onload = adjustCoords;
      
      // 이미 로드된 경우를 위한 초기 조정
      adjustCoords();
    }
    
    // 윈도우 리사이즈 이벤트에 대응
    const handleResize = () => {
      requestAnimationFrame(adjustCoords);
    };
    
    window.addEventListener('resize', handleResize);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(()=>{
    if(imgRef.current){
      imgRef.current.onload = adjustCoords;
      adjustCoords();
    }
  }, [imgRef.current]);

  

  return (<ResponsiveContainer ref={containerRef}>
    <ResponsiveImage 
      src="/images/landing.png?v=3" 
      alt="제휴 안내" 
      ref={imgRef}
    />

    {clickAreas.map((area, index) => (
      <a href={areas[index].url} target="_blank" key={area.id}>
        <ClickArea 
          key={area.id}
          style={area.style}
          onClick={() => handleLinkClick(area.id, areas[index].url)}
        />
      </a>
    ))}

    {loading && (
      <LoadingOverlay>
        <LoadingSpinner />
      </LoadingOverlay>
    )}
  </ResponsiveContainer>
  );
};

const J_MainLandingPage: React.FC<{pageId: string}> = ({pageId}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  const [areas] = useState([
    { id: '허슬러카지노', url: 'https://허슬러접속.com', coords: [0, 530, 1000, 820], shape:"rect" as const },
    { id: '크크벳', url: 'https://크크벳접속.com', coords: [0, 840, 1000, 1130], shape:"rect" as const },
    { id: '벳마트', url: 'https://벳마트.com', coords: [0, 1150, 1000, 1440], shape:"rect" as const },
    { id: '야옹이', url: 'https://야옹이.com', coords: [0, 1465, 1000, 1750], shape:"rect" as const },
    { id: '대빵', url: 'https://대빵.com', coords: [0, 1780, 1000, 2070], shape:"rect" as const },
    { id: '정글', url: 'https://정글접속.com', coords: [0, 2100, 1000, 2385], shape:"rect" as const },
    { id: '당근벳', url: 'https://당근접속.com', coords: [0, 2420, 1000, 2710], shape:"react" as const },
    { id: '땅콩', url: 'https://땅콩접속.com', coords: [0, 2745, 1000, 3040], shape:"react" as const },
    { id: '에그벳', url: 'https://에그에그.com', coords: [0, 3070, 1000, 3360], shape:"react" as const },
    { id: '펩시', url: 'https://펩시접속.com', coords: [0, 3390, 1000, 3680], shape:"react" as const },
    { id: '반딧불', url: 'https://반딧불.com', coords: [0, 3720, 1000, 4010], shape:"react" as const },
    { id: '에이피', url: 'https://에이피.com', coords: [0, 4050, 1000, 4340], shape:"react" as const },
    { id: '돛단배', url: 'https://돛단배.com', coords: [0, 4380, 1000, 4670], shape:"react" as const },
    { id: '이태리', url: 'https://이태리.com', coords: [0, 4710, 1000, 5000], shape:"react" as const },
    { id: '썬벳', url: 'https://썬뱃.com', coords: [0, 5030, 1000, 5330], shape:"react" as const },
  ]);
  const [clickAreas, setClickAreas] = useState<{id: string; style: {left: string; top: string; width: string; height: string;}}[]>([]);
  
  // 원본 이미지 크기 정의
  const originalWidth = 1000;
  const originalHeight = 3431;

  useEffect(() => {
    // 모바일 여부 확인
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    // 페이지 조회수 증가
    const recordView = async () => {
      try {
        await landingAPI.recordView(pageId || 'QLANDING');
      } catch (error) {
        console.error('페이지 조회수 기록 중 오류 발생:', error);
      }
    };

    recordView();
  }, [pageId]);

  // 링크 클릭 핸들러
  const handleLinkClick = async (linkId: string, url: string) => {
    setLoading(true);
    try {
      landingAPI.recordClick(pageId!, linkId);
    } catch (error) {
    }
    setTimeout(()=>setLoading(false), 1000);
  };

  // 좌표 조정 함수
  const adjustCoords = () => {
    if (!imgRef.current) return;
    
    const currentWidth = imgRef.current.clientWidth;
    const ratio = currentWidth / originalWidth;
    
    const newClickAreas = areas.map(area => {
      const [x1, y1, x2, y2] = area.coords;
      return {
        id: area.id,
        style: {
          left: `${(x1 * ratio)}px`,
          top: `${(y1 * ratio)}px`,
          width: `${(x2 - x1) * ratio}px`,
          height: `${(y2 - y1) * ratio}px`
        }
      };
    });
    
    setClickAreas(newClickAreas);
  };

  // 컴포넌트 마운트 및 리사이즈 시 좌표 조정
  useEffect(() => {
    // 이미지 로드 완료 시 좌표 조정
    if (imgRef.current) {
      imgRef.current.onload = adjustCoords;
      
      // 이미 로드된 경우를 위한 초기 조정
      adjustCoords();
    }
    
    // 윈도우 리사이즈 이벤트에 대응
    const handleResize = () => {
      requestAnimationFrame(adjustCoords);
    };
    
    window.addEventListener('resize', handleResize);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(()=>{
    if(imgRef.current){
      imgRef.current.onload = adjustCoords;
      adjustCoords();
    }
  }, [imgRef.current]);

  return (<ResponsiveContainer ref={containerRef}>
    <ResponsiveImage 
      src="/images/landingJ.gif" 
      alt="제휴 안내" 
      ref={imgRef}
    />

    {clickAreas.map((area, index) => (
      <a href={areas[index].url} target="_blank" key={area.id}>
        <ClickArea 
          key={area.id}
          style={area.style}
          onClick={() => handleLinkClick(area.id, areas[index].url)}
        />
      </a>
    ))}

    {loading && (
      <LoadingOverlay>
        <LoadingSpinner />
      </LoadingOverlay>
    )}
  </ResponsiveContainer>
  );
};

const OtherBannerLandingPage: React.FC<{pageId: string}> = ({pageId}) => {
  useEffect(()=>{
    const recordView = async () => {
      try {
        await landingAPI.recordView(pageId || "O");
      } catch (error) {
        console.error('페이지 조회수 기록 중 오류 발생:', error);
      }
    };

    recordView();
  },[pageId]);

  const handleLinkClick = async (linkId: string, url: string) => {
    try {
      landingAPI.recordClick(pageId!, linkId);
    } catch (error) {
    }
  };

  const key = pageId.replace("/","").slice(0,1);
  const banner = bannerUrls[key as keyof typeof bannerUrls];

  return (<ResponsiveContainer>
    <a href={banner.url} target="_blank">
      <ResponsiveImage onClick={()=>handleLinkClick(key, banner.url)} src={banner.image} alt="제휴 안내" />
    </a>
  </ResponsiveContainer>
  );
}

export const banners = [
  "Q", // 기존 메인 배너
  "W","E","R","T","Y","U","I","O","P","A","S","D","F","G","H", // 15개 개별 배너
  "J", // 추가 메인 배너 
  "K","L","Z","X","C","V","B","N","M"
]
const bannerUrls = {
  Q:{image:"/", url:"main banner"},
  J:{image:"/", url:"main banner"},
  W:{image:"/images/sites/long/Banner_1.jpg", url:"https://돛단배.com"},
  E:{image:"/images/sites/long/Banner_2.jpg", url:"https://대빵.com"},
  R:{image:"/images/sites/long/Banner_3.jpg", url:"https://크크벳.com"},
  T:{image:"/images/sites/long/Banner_4.jpg", url:"https://이태리.com"},
  Y:{image:"/images/sites/long/Banner_5.jpg", url:"https://펩시접속.com"},
  U:{image:"/images/sites/long/Banner_6.jpg", url:"https://정글주소.com"},
  I:{image:"/images/sites/long/Banner_7.jpg", url:"https://에이피.com"},
  O:{image:"/images/sites/long/Banner_8.jpg", url:"https://벳마트.com"},
  P:{image:"/images/sites/long/Banner_9.jpg", url:"https://당근접속.com"},
  A:{image:"/images/sites/long/Banner_10.jpg", url:"https://에그에그.com"},
  S:{image:"/images/sites/long/Banner_11.jpg", url:"https://썬뱃.com"},
  D:{image:"/images/sites/long/Banner_12.jpg", url:"https://야옹이.com"},
  F:{image:"/images/sites/long/Banner_13.jpg", url:"https://반딧불.com"},
  G:{image:"/images/sites/long/Banner_14.jpg", url:"https://땅콩접속.com"},
  H:{image:"/images/sites/long/Banner_15.jpg", url:"https://허슬러접속.com"},
}

const LandingPage: React.FC = () => {
  const hostname = window.location.hostname;
  const isKoreanDomain = hostname === "xn--le5b23cccq68e.com"; // 안전제휴
  const isKoreanDomain2 = hostname === "xn--c79ay38b92b12b.com"; // 안전검증 -> 크크벳으로 몰빵

  const location = useLocation();
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const isQPage = useMemo(()=>{
    for(const banner of banners){
      if(location.pathname.startsWith(`/${banner}`) && !location.pathname.startsWith(`/${banner}EVENT`)){
        return true;
      }
    }
    return false;
  },[location.pathname]);

  useEffect(()=>{
    const key = location.pathname.slice(1,2);
    const banner = bannerUrls[key as keyof typeof bannerUrls];
    if(!banner && !isKoreanDomain && !isKoreanDomain2){
      navigate('/404');
      return;
    }
  },[location])

  const startsWithQ = useMemo(()=>{
    return location.pathname.startsWith('/Q');
  },[location.pathname]);

  const startsWithJ = useMemo(()=>{
    return location.pathname.startsWith('/J');
  },[location.pathname]);

  const IsQ = startsWithQ
  const IsJ = startsWithJ

  return (
    <div className={styles.landingPageContainer} style={(isQPage) ? {padding: '0'} : {}}>
      {isKoreanDomain2 ? <>
        <OtherBannerLandingPage pageId={"REVENT"} />
      </> : <>
        {(IsQ) ? <Q_MainLandingPage pageId={pageId || ''} /> : null}
        {(IsJ || isKoreanDomain) ? <J_MainLandingPage pageId={pageId || 'JJJ'} /> : null}
        {(!IsJ && !IsQ && !isKoreanDomain) ? <OtherBannerLandingPage pageId={pageId || ''} /> : null}
      </>}
    </div>
  );
};

export default LandingPage; 